import React, { useState } from 'react';
import { X, Info, Search } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DownloadReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  deduplication: 'deduplicated' | 'raw';
}
type ReportFormat = 'itemized' | 'summary';
type ColumnsView = 'default' | 'all' | 'custom';
const ITEMIZED_COLUMNS = [{
  id: 'charge_id',
  label: 'Charge ID',
  group: 'left'
}, {
  id: 'payment_intent_id',
  label: 'Payment Intent ID',
  group: 'left'
}, {
  id: 'invoice_id',
  label: 'Invoice ID',
  group: 'left'
}, {
  id: 'created',
  label: 'Created',
  group: 'left'
}, {
  id: 'currency',
  label: 'Currency',
  group: 'left'
}, {
  id: 'presentment_amount',
  label: 'Presentment amount',
  group: 'left'
}, {
  id: 'card_brand',
  label: 'Card brand',
  group: 'left'
}, {
  id: 'card_type',
  label: 'Card type',
  group: 'left'
}, {
  id: 'card_input_method',
  label: 'Card input method',
  group: 'left'
}, {
  id: 'card_country',
  label: 'Card country',
  group: 'left'
}, {
  id: 'outcome_type',
  label: 'Outcome type',
  group: 'left'
}, {
  id: 'blocked_reason',
  label: 'Blocked reason',
  group: 'right'
}, {
  id: 'decline_reason',
  label: 'Decline reason',
  group: 'right'
}, {
  id: 'attributable_optimization',
  label: 'Attributable optimization',
  group: 'right'
}, {
  id: 'final_attempt',
  label: 'Final attempt',
  group: 'right'
}, {
  id: 'final_charge_id',
  label: 'Final charge ID',
  group: 'right'
}, {
  id: 'used_network_tokens',
  label: 'Used network tokens',
  group: 'right'
}, {
  id: 'interaction_type',
  label: 'Interaction type',
  group: 'right'
}, {
  id: 'retry_status',
  label: 'Retry status',
  group: 'right'
}, {
  id: 'avs_response',
  label: 'AVS Response',
  group: 'right'
}, {
  id: 'cvc_response',
  label: 'CVC Response',
  group: 'right'
}, {
  id: '3ds_outcome',
  label: '3DS outcome',
  group: 'right'
}];
const SUMMARY_COLUMNS = [{
  id: 'start_time',
  label: 'Start time for period'
}, {
  id: 'report_currency',
  label: 'Report currency'
}, {
  id: 'pivot_column',
  label: 'Pivot column selected'
}, {
  id: 'total_authorized_count',
  label: 'Total authorized count'
}, {
  id: 'total_payment_count',
  label: 'Total payment count'
}, {
  id: 'total_authorized_amount',
  label: 'Total authorized amount'
}, {
  id: 'total_payment_amount',
  label: 'Total payment amount'
}];
export function DownloadReportModal({
  isOpen,
  onClose,
  dateRange,
  deduplication
}: DownloadReportModalProps) {
  const [reportFormat, setReportFormat] = useState<ReportFormat>('itemized');
  const [columnsView, setColumnsView] = useState<ColumnsView>('default');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [emailNotification, setEmailNotification] = useState(false);
  if (!isOpen) return null;
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const dateRangeText = `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`;
  const getInfoMessage = () => {
    if (deduplication === 'raw') {
      return 'This report shows raw data, which includes all attempted card payments.';
    }
    return 'This report shows only final attempt payments. To see all payments, switch to the raw view.';
  };
  const currentColumns = reportFormat === 'itemized' ? ITEMIZED_COLUMNS : SUMMARY_COLUMNS;
  const filteredColumns = currentColumns.filter(col => col.label.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleColumn = (columnId: string) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(columnId)) {
      newSelected.delete(columnId);
    } else {
      newSelected.add(columnId);
    }
    setSelectedColumns(newSelected);
  };
  const selectAllColumns = () => {
    setSelectedColumns(new Set(currentColumns.map(col => col.id)));
  };
  const deselectAllColumns = () => {
    setSelectedColumns(new Set());
  };
  const handleShowColumns = () => {
    if (columnsView === 'default') {
      setColumnsView('custom');
      // Pre-select all columns when showing
      selectAllColumns();
    } else {
      setColumnsView('default');
    }
  };
  const getColumnCount = () => {
    if (columnsView === 'default') {
      return reportFormat === 'itemized' ? 22 : 7;
    }
    return selectedColumns.size;
  };
  const handleDownload = () => {
    // Implement download logic here
    console.log('Downloading report...', {
      dateRange: dateRangeText,
      deduplication,
      reportFormat,
      columnsView,
      selectedColumns: Array.from(selectedColumns),
      emailNotification
    });
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-860">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-861">
        <div className="p-6" data-id="element-862">
          {/* Header */}
          <div className="flex items-center justify-between mb-6" data-id="element-863">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-864">
              Download report — {dateRangeText}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-865">
              <X size={20} data-id="element-866" />
            </button>
          </div>

          {/* Info Banner */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex gap-3" data-id="element-867">
            <Info size={20} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-868" />
            <p className="text-sm text-gray-700" data-id="element-869">{getInfoMessage()}</p>
          </div>

          {/* Report Format */}
          <div className="mb-6" data-id="element-870">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-871">
              Report format
            </h3>
            <div className="flex gap-4" data-id="element-872">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-873">
                <input type="radio" name="format" checked={reportFormat === 'itemized'} onChange={() => {
                setReportFormat('itemized');
                setColumnsView('default');
                setSelectedColumns(new Set());
              }} className="w-4 h-4 text-blue-600" data-id="element-874" />
                <span className="text-sm text-gray-900" data-id="element-875">Itemized</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-876">
                <input type="radio" name="format" checked={reportFormat === 'summary'} onChange={() => {
                setReportFormat('summary');
                setColumnsView('default');
                setSelectedColumns(new Set());
              }} className="w-4 h-4 text-blue-600" data-id="element-877" />
                <span className="text-sm text-gray-900" data-id="element-878">Summary</span>
              </label>
            </div>
          </div>

          {/* Columns */}
          <div className="mb-6" data-id="element-879">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-880">Columns</h3>
            <div className="flex items-center gap-2 mb-3" data-id="element-881">
              <select className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" value={`all-${getColumnCount()}`} disabled data-id="element-882">
                <option data-id="element-883">All ({getColumnCount()})</option>
              </select>
              <button onClick={handleShowColumns} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2" data-id="element-884">
                {columnsView === 'default' ? <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-885">
                      <path d="M8 3.5V12.5M8 12.5L11.5 9M8 12.5L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-886" />
                    </svg>
                    Show
                  </> : <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-887">
                      <path d="M8 12.5V3.5M8 3.5L4.5 7M8 3.5L11.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-888" />
                    </svg>
                    Hide
                  </>}
              </button>
            </div>

            {/* Column Selection */}
            {columnsView === 'custom' && <div className="border border-gray-200 rounded-lg" data-id="element-889">
                {/* Search */}
                <div className="p-3 border-b border-gray-200" data-id="element-890">
                  <div className="relative" data-id="element-891">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-892" />
                    <input type="text" placeholder="Filter by name or metadata key" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-893" />
                  </div>
                </div>

                {/* Column List */}
                <div className="max-h-80 overflow-y-auto p-3" data-id="element-894">
                  {reportFormat === 'itemized' ? <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-895">
                      {/* Left Column */}
                      <div data-id="element-896">
                        {filteredColumns.filter(col => col.group === 'left').map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-897">
                              <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-898" />
                              <span className="text-sm text-gray-900" data-id="element-899">
                                {column.label}
                              </span>
                            </label>)}
                      </div>

                      {/* Right Column */}
                      <div data-id="element-900">
                        {filteredColumns.filter(col => col.group === 'right').map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-901">
                              <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-902" />
                              <span className="text-sm text-gray-900" data-id="element-903">
                                {column.label}
                              </span>
                            </label>)}
                      </div>
                    </div> : <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-904">
                      {filteredColumns.map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-905">
                          <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-906" />
                          <span className="text-sm text-gray-900" data-id="element-907">
                            {column.label}
                          </span>
                        </label>)}
                    </div>}
                </div>
              </div>}
          </div>

          {/* Notifications */}
          <div className="mb-6" data-id="element-908">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-909">
              Notifications
            </h3>
            <label className="flex items-start gap-2 cursor-pointer" data-id="element-910">
              <input type="checkbox" checked={emailNotification} onChange={e => setEmailNotification(e.target.checked)} className="w-4 h-4 text-blue-600 rounded mt-0.5" data-id="element-911" />
              <span className="text-sm text-gray-700" data-id="element-912">
                Email simon.verraest@ceylontea.com when your report is ready to
                download
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-913">
            <p className="text-sm text-gray-600" data-id="element-914">
              The file will be saved as .csv.
            </p>
            <div className="flex items-center gap-3" data-id="element-915">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg" data-id="element-916">
                Cancel
              </button>
              <button onClick={handleDownload} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-917">
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}